import { useState, useEffect } from "react";
import {
  Upload,
  Trash2,
  Star,
  ArrowLeft,
  Save,
  Home,
  FileText,
  ImagePlus,
} from "lucide-react";
import { useCorporateInteriorDesign } from "../../useHook";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { sanitizePositionInput } from "../../../../Utils/positionInput";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../Components/Shared/ImageSeoFields";
import { RichTextEditor } from "../../../../Components/Shared/RichTextEditor";
import {
  DESIGN_TYPE_OPTIONS,
  urduFor,
} from "../../../../Utils/staticDropdownOptions";

/** Shared create/edit form — uses RichTextEditor (not ReactQuill). */
const emptyFormData = {
  title: "",
  title_ur: "",
  description: "",
  description_ur: "",
  customUrl: "",
  plot_dimension: "",
  covered_area: "",
  floors: 0,
  bed_rooms: 0,
  bath_rooms: 0,
  room_size: "",
  design_type: "",
  design_type_ur: "",
  images: [],
  imagePreviews: [],
  imagePositions: [],
  imageAlts: [],
  imageTitles: [],
  primaryImageIndex: 0,
  features: {
    carParking: false,
    terrace: false,
    servantQuarter: false,
  },
};

const cardIcon =
  "inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#982598]/10 text-[#982598]";

export const InteriorDesignForm = ({ isEditMode = false }) => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const {
    createCorporateInteriorDesign,
    updateCorporateInteriorDesign,
    getCorporateInteriorDesignById,
  } = useCorporateInteriorDesign();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");
  const [formData, setFormData] = useState({ ...emptyFormData });
  const [existingImages, setExistingImages] = useState([]);
  const [existingImagePositions, setExistingImagePositions] = useState({});
  const [primaryExistingImageId, setPrimaryExistingImageId] = useState(null);

  useEffect(() => {
    if (isEditMode && editId) {
      loadDesignForEdit(editId);
    }
  }, [isEditMode, editId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const nextValue =
        type === "checkbox"
          ? checked
          : type === "number"
            ? parseInt(value, 10) || 0
            : value;
      const next = { ...prev, [name]: nextValue };
      if (name === "design_type") {
        next.design_type_ur = urduFor(DESIGN_TYPE_OPTIONS, nextValue);
      }
      return next;
    });
  };

  const handleDescriptionChange = (value) => {
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleDescriptionUrChange = (value) => {
    setFormData((prev) => ({ ...prev, description_ur: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const checkImageResolution = (file) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width === 1200 && img.height === 900);
        img.src = URL.createObjectURL(file);
      });

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        const newValidImages = [];
        const newImagePreviews = [];
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            alert(
              `Image "${files[index].name}" does not have the required resolution of 1200*900. Please upload the correct resolution.`,
            );
          }
        });
        if (!newValidImages.length) return;
        setImageError("");
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex =
            currentImageCount === 0 ? 0 : prev.primaryImageIndex;
          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            imageAlts: [
              ...(prev.imageAlts || []),
              ...newValidImages.map(() => ""),
            ],
            imageTitles: [
              ...(prev.imageTitles || []),
              ...newValidImages.map(() => ""),
            ],
            imagePositions: [
              ...(prev.imagePositions || []),
              ...Array(newValidImages.length).fill(""),
            ],
            primaryImageIndex: newPrimaryIndex,
          };
        });
      },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith("/")) {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if (
      (!formData.images || formData.images.length === 0) &&
      !(isEditMode && existingImages.length > 0)
    ) {
      setImageError("At least one image is required");
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    try {
      const apiData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (
          [
            "images",
            "imagePreviews",
            "primaryImageIndex",
            "imagePositions",
            "imageAlts",
            "imageTitles",
            "existingImages",
            "existingImagePositions",
            "primaryExistingImageId",
            "category_id",
          ].includes(key)
        ) {
          return;
        }
        if (key === "features") {
          apiData.append(key, JSON.stringify(formData[key]));
        } else {
          apiData.append(key, formData[key]);
        }
      });

      if (formData.primaryImageIndex != null) {
        apiData.append("primaryImageIndex", formData.primaryImageIndex);
      }
      if (formData.imagePositions?.length > 0) {
        formData.imagePositions.forEach((pos) => {
          apiData.append("imagePositions", pos);
        });
      }
      if (formData.images?.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append("image", image);
          apiData.append(
            "imageAlt",
            String(formData.imageAlts?.[index] ?? "").trim(),
          );
          apiData.append(
            "imageTitle",
            String(formData.imageTitles?.[index] ?? "").trim(),
          );
        });
      }

      if (isEditMode) {
        apiData.append(
          "keepImageIds",
          JSON.stringify(existingImages.map((img) => img.id)),
        );
        const existingImageAlts = {};
        const existingImageTitles = {};
        existingImages.forEach((img) => {
          if (img.id == null) return;
          if (img.image_alt != null && String(img.image_alt).trim()) {
            existingImageAlts[img.id] = String(img.image_alt).trim();
          }
          if (img.image_title != null && String(img.image_title).trim()) {
            existingImageTitles[img.id] = String(img.image_title).trim();
          }
        });
        if (Object.keys(existingImageAlts).length > 0) {
          apiData.append("existingImageAlts", JSON.stringify(existingImageAlts));
        }
        if (Object.keys(existingImageTitles).length > 0) {
          apiData.append(
            "existingImageTitles",
            JSON.stringify(existingImageTitles),
          );
        }
        apiData.append(
          "existingImagePositions",
          JSON.stringify(existingImagePositions),
        );
        if (primaryExistingImageId != null) {
          apiData.append("primaryExistingImageId", primaryExistingImageId);
        }
      }

      const response = isEditMode
        ? await updateCorporateInteriorDesign(editId, apiData)
        : await createCorporateInteriorDesign(apiData);

      if (response && response.success === false) {
        throw new Error(
          response.message || "Failed to save corporate interior design.",
        );
      }

      resetForm();
      Swal.fire({
        icon: "success",
        title: isEditMode
          ? "Corporate Interior Design Updated"
          : "Corporate Interior Design Added",
        showConfirmButton: false,
        timer: 1500,
      });
      navigate("/corporate-interior-design");
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message ||
          "Failed to save corporate interior design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    (formData.imagePreviews || []).forEach((url) => URL.revokeObjectURL(url));
    setFormData({ ...emptyFormData, features: { ...emptyFormData.features } });
    setExistingImages([]);
    setExistingImagePositions({});
    setPrimaryExistingImageId(null);
  };

  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    const updatedPreviews = [...(formData.imagePreviews || [])];
    const updatedPositions = [...(formData.imagePositions || [])];
    const updatedAlts = [...(formData.imageAlts || [])];
    const updatedTitles = [...(formData.imageTitles || [])];
    if (updatedPreviews[index]) URL.revokeObjectURL(updatedPreviews[index]);
    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);
    updatedPositions.splice(index, 1);
    updatedAlts.splice(index, 1);
    updatedTitles.splice(index, 1);
    let newPrimaryIndex = formData.primaryImageIndex;
    if (index === formData.primaryImageIndex) {
      newPrimaryIndex = null;
    } else if (index < formData.primaryImageIndex) {
      newPrimaryIndex = formData.primaryImageIndex - 1;
    }
    setFormData({
      ...formData,
      images: updatedImages,
      imagePreviews: updatedPreviews,
      imagePositions: updatedPositions,
      imageAlts: updatedAlts,
      imageTitles: updatedTitles,
      primaryImageIndex: newPrimaryIndex,
    });
    if (updatedImages.length === 0 && existingImages.length === 0) {
      setImageError("At least one image is required");
    }
  };

  const setPrimaryImage = (index) => {
    setFormData((prev) => ({ ...prev, primaryImageIndex: index }));
    setPrimaryExistingImageId(null);
  };

  const setPrimaryExistingImage = (id) => {
    setPrimaryExistingImageId(id);
    setFormData((prev) => ({ ...prev, primaryImageIndex: null }));
  };

  const onImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const positions = [...(prev.imagePositions || [])];
      positions[index] = cleaned;
      return { ...prev, imagePositions: positions };
    });
  };

  const onNewImageSeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "imageAlts" : "imageTitles";
    setFormData((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const loadDesignForEdit = async (id) => {
    try {
      setIsSubmitting(true);
      const design = await getCorporateInteriorDesignById(id);
      if (!design) throw new Error("Corporate interior design not found");
      setFormData({
        title: design.title || "",
        title_ur: design.title_ur || "",
        description: design.description || "",
        description_ur: design.description_ur || "",
        customUrl: design.custom_url || design.customUrl || "",
        plot_dimension: design.plot_dimension || "",
        covered_area: design.covered_area || "",
        floors: design.floors || 0,
        bed_rooms: design.bed_rooms || 0,
        bath_rooms: design.bath_rooms || 0,
        room_size: design.room_size || "",
        design_type: design.design_type || "",
        design_type_ur: design.design_type_ur || "",
        images: [],
        imagePreviews: [],
        imagePositions: [],
        imageAlts: [],
        imageTitles: [],
        primaryImageIndex: null,
        features: {
          carParking:
            design.car_parking || design.features?.carParking || false,
          terrace: design.terrace || design.features?.terrace || false,
          servantQuarter:
            design.servant_quarters || design.features?.servantQuarter || false,
        },
      });
      const existingImgs = design.existingImages || design.images || [];
      setExistingImages(existingImgs);
      const positions = {};
      let primaryId = null;
      existingImgs.forEach((img) => {
        if (img.position) positions[img.id] = img.position;
        if (img.is_primary) primaryId = img.id;
      });
      setExistingImagePositions(positions);
      setPrimaryExistingImageId(primaryId);
      setImageError("");
    } catch (error) {
      console.error("Failed to load corporate interior design for edit:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message ||
          "Failed to load corporate interior design for editing.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      (formData.imagePreviews || []).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [formData.imagePreviews]);

  const handleCancel = () => {
    resetForm();
    navigate("/corporate-interior-design");
  };

  const renderActionButtons = () => (
    <>
      <button
        type="button"
        onClick={handleCancel}
        className="ui-btn-cancel"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button type="submit" className="ui-btn-save" disabled={isSubmitting}>
        <Save size={14} />
        {isSubmitting
          ? isEditMode
            ? "Updating..."
            : "Saving..."
          : isEditMode
            ? "Update"
            : "Save"}
      </button>
    </>
  );

  return (
    <div className="house-design-form-page min-h-0">
      <form onSubmit={handleSubmit}>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="ui-btn-back"
              aria-label="Back"
              disabled={isSubmitting}
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <p className="mb-0 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#982598]">
                Corporate Interior Design
              </p>
              <h2 className="text-xl font-extrabold tracking-tight text-[#15173d]">
                {isEditMode
                  ? "Edit Corporate Interior Design"
                  : "Add Corporate Interior Design"}
              </h2>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#15173d]/10 bg-white px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wide text-[#15173d]">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                isEditMode ? "bg-[#982598]" : "bg-green-600"
              }`}
            />
            {isEditMode ? "Edit" : "Create"}
          </span>
        </div>

        <div className="space-y-4">
          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <Home size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Basic details
                </h3>
                <p className="text-[11px] text-gray-500">
                  Title, type, room size &amp; URL
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="ui-form-label">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
                <UrduInput
                  label="عنوان"
                  name="title_ur"
                  value={formData.title_ur}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="ui-form-label">
                  Custom Url <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customUrl"
                  value={formData.customUrl}
                  onChange={handleChange}
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">
                  Room Size <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="room_size"
                  value={formData.room_size}
                  onChange={handleChange}
                  placeholder="e.g., 12x15 ft"
                  className="ui-input-field"
                  required
                />
              </div>
              <div>
                <label className="ui-form-label">
                  Design Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="design_type"
                  value={formData.design_type}
                  onChange={handleChange}
                  className="ui-select-field"
                  required
                >
                  <option value="">Select design type</option>
                  {DESIGN_TYPE_OPTIONS.map((o) => (
                    <option key={o.en} value={o.en}>
                      {o.en}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="ui-form-label text-right" dir="rtl">
                  ڈیزائن کی قسم
                </label>
                <select
                  name="design_type_ur"
                  value={formData.design_type_ur}
                  onChange={handleChange}
                  dir="rtl"
                  className="ui-select-field"
                >
                  <option value="">منتخب کریں...</option>
                  {DESIGN_TYPE_OPTIONS.map((o) => (
                    <option key={o.ur} value={o.ur}>
                      {o.ur}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <FileText size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Description
                </h3>
                <p className="text-[11px] text-gray-500">
                  English &amp; Urdu content
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="ui-form-label">
                  Description <span className="text-red-500">*</span>
                </label>
                <RichTextEditor
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="Write corporate interior design description..."
                  minHeight={180}
                  maxHeight={420}
                />
              </div>
              <div>
                <label className="ui-form-label text-right" dir="rtl">
                  تفصیل
                </label>
                <RichTextEditor
                  dir="rtl"
                  value={formData.description_ur}
                  onChange={handleDescriptionUrChange}
                  placeholder="اردو میں تفصیل لکھیں..."
                  minHeight={180}
                  maxHeight={420}
                />
              </div>
            </div>
          </section>

          <section className="ui-card">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={cardIcon}>
                <ImagePlus size={15} />
              </span>
              <div>
                <h3 className="text-[14px] font-extrabold text-[#15173d]">
                  Gallery
                </h3>
                <p className="text-[11px] text-gray-500">
                  1200×900 images · set primary &amp; positions
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="ui-form-label">
                Upload Images <span className="text-red-500">*</span>
              </label>
              <div className="rounded-2xl border-2 border-dashed border-[#982598]/35 bg-[rgba(152,37,152,0.03)] p-8 transition hover:border-[#982598]">
                <input
                  type="file"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="corporate-interior-images"
                  accept="image/*"
                />
                <label
                  htmlFor="corporate-interior-images"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#982598]/10 text-[#982598]">
                    <Upload size={22} />
                  </span>
                  <span className="text-[13px] font-extrabold text-[#15173d]">
                    Click to upload multiple images
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Only 1200×900 resolution accepted
                  </span>
                </label>
              </div>
            </div>

            {isEditMode && existingImages.length > 0 && (
              <div className="mb-4">
                <h4 className="ui-form-label">
                  Existing Images ({existingImages.length})
                </h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {existingImages.map((image, index) => (
                    <div key={image.id || index} className="group relative">
                      <div
                        className={`relative flex h-24 items-center justify-center overflow-hidden rounded-xl border border-[#15173d]/10 bg-[#faf8fa] ${
                          primaryExistingImageId === image.id
                            ? "ring-2 ring-[#982598]"
                            : ""
                        }`}
                      >
                        <img
                          src={image.image_url || image.url || image}
                          alt={`Existing ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                        {primaryExistingImageId === image.id && (
                          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded bg-[#982598] px-1.5 py-0.5 text-[10px] font-bold text-white">
                            PRIMARY
                          </div>
                        )}
                      </div>
                      <div className="absolute right-1 top-1 flex gap-1 opacity-0 group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => setPrimaryExistingImage(image.id)}
                          className={`rounded-full p-1 shadow-md ${
                            primaryExistingImageId === image.id
                              ? "bg-[#982598] text-white"
                              : "bg-white text-gray-600"
                          }`}
                        >
                          <Star
                            size={12}
                            fill={
                              primaryExistingImageId === image.id
                                ? "currentColor"
                                : "none"
                            }
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setExistingImages((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                          className="rounded-full bg-red-500 p-1 text-white"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      {primaryExistingImageId !== image.id && (
                        <div className="mt-1">
                          <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                            Position
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={existingImagePositions[image.id] ?? ""}
                            onChange={(e) =>
                              setExistingImagePositions((prev) => ({
                                ...prev,
                                [image.id]: sanitizePositionInput(
                                  e.target.value,
                                ),
                              }))
                            }
                            className="ui-input-field w-full px-1 py-1 text-xs"
                          />
                        </div>
                      )}
                      <ImageSeoFields
                        stacked
                        imageAlt={image.image_alt || ""}
                        imageTitle={image.image_title || ""}
                        onAltChange={(value) =>
                          setExistingImages((prev) =>
                            prev.map((img, i) =>
                              i === index ? { ...img, image_alt: value } : img,
                            ),
                          )
                        }
                        onTitleChange={(value) =>
                          setExistingImages((prev) =>
                            prev.map((img, i) =>
                              i === index
                                ? { ...img, image_title: value }
                                : img,
                            ),
                          )
                        }
                        className="mt-2"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {formData.images.length > 0 && (
              <div className="mb-4">
                <h4 className="ui-form-label">
                  Selected Images ({formData.images.length})
                </h4>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {formData.images.map((file, index) => (
                    <div key={index} className="relative">
                      <div
                        className={`relative flex h-24 overflow-hidden rounded-xl border border-[#15173d]/10 bg-[#faf8fa] ${
                          index === formData.primaryImageIndex
                            ? "ring-2 ring-[#982598]"
                            : ""
                        }`}
                      >
                        {formData.imagePreviews?.[index] ? (
                          <img
                            src={formData.imagePreviews[index]}
                            alt={file.name || `Image ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                        ) : null}
                        {index === formData.primaryImageIndex && (
                          <div className="absolute left-1 top-1 rounded-full bg-[#982598] p-1 text-white">
                            <Star size={12} fill="white" />
                          </div>
                        )}
                        {index !== formData.primaryImageIndex && (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(index)}
                            className="absolute bottom-1 left-1 rounded-full bg-white p-1 text-gray-600 shadow-md"
                          >
                            <Star size={12} />
                          </button>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -right-2 -top-2 z-10 rounded-full bg-red-500 p-1 text-white"
                      >
                        <Trash2 size={12} />
                      </button>
                      {index !== formData.primaryImageIndex && (
                        <div className="mt-1">
                          <label className="ui-form-label mb-0.5 normal-case tracking-normal">
                            Position
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formData.imagePositions?.[index] ?? ""}
                            onChange={(e) =>
                              onImagePositionChange(index, e.target.value)
                            }
                            className="ui-input-field w-full px-1 py-1 text-xs"
                          />
                        </div>
                      )}
                      <ImageSeoFields
                        stacked
                        imageAlt={formData.imageAlts?.[index] || ""}
                        imageTitle={formData.imageTitles?.[index] || ""}
                        onAltChange={(value) =>
                          onNewImageSeoChange(index, "image_alt", value)
                        }
                        onTitleChange={(value) =>
                          onNewImageSeoChange(index, "image_title", value)
                        }
                        className="mt-2"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {imageError ? (
              <p className="mb-2 text-sm text-red-500">{imageError}</p>
            ) : null}

            <div className="mt-6 flex justify-end gap-2 border-t border-[#15173d]/[0.07] pt-4">
              {renderActionButtons()}
            </div>
          </section>
        </div>
      </form>
    </div>
  );
};

export default InteriorDesignForm;
